import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { Row } from 'antd'
import _ from 'lodash'

import CustomInput from '../../../../../../components/CustomInput/customInput'
import CustomSelect from '../../../../../../components/CustomSelect/customSelect'
import CustomSwitch from '../../../../../../components/CustomSwitch/customSwitch'
import { getMessage } from '../../../../../../util/IntlMessages'
import { isAdmin, isMemberLevel } from '../../../../../../util/profiles'
import { actionStatus } from '../../constants'
import { useCreateActionContext } from '../../contexts/action'
import {
  useGetActionStatusQuery,
  useGetActionTypesQuery,
  useGetCustomersQuery,
  useGetHarvestsQuery,
  useGetLocationsQuery,
  useGetMembersQuery
} from '../../services/field'

export function BaseFields({ values, errors, handleBlur, setValues }) {
  const [formValues, setFormValues] = useState({})
  const location = useLocation()
  const isForecastPreload = location.state?.source === 'forecast-visit-modal'
  const memberAutoSelectedRef = useRef(false)
  const locationAutoSelectedRef = useRef(false)
  const previousActionIdRef = useRef(null)
  const { isNewAction, isPreview, isReadOnly, currentUser, actionId } = useCreateActionContext()

  useEffect(
    function updateFormValues() {
      if (previousActionIdRef.current !== actionId) {
        previousActionIdRef.current = actionId
        setFormValues(values)
        return
      }

      if (!Object.keys(formValues).length) {
        setFormValues(values)
        return
      }

      if (formValues.actionStatusId !== values.actionStatusId) {
        setFormValues((prev) => ({ ...prev, actionStatusId: values.actionStatusId }))
      }
    },
    [actionId, formValues, values]
  )

  const debouncedSetValues = useRef(_.debounce(setValues, 200)).current

  useEffect(() => () => debouncedSetValues.cancel(), [debouncedSetValues])

  function onChangeValue(fieldName, value) {
    let newValues = { ...formValues, [fieldName]: value }

    if (fieldName === 'actionTypeId') {
      newValues = _.omit(newValues, ['customerId', 'memberId', 'locationId'])
    }

    if (fieldName === 'customerId') {
      if (value === undefined) {
        memberAutoSelectedRef.current = true
        newValues.memberId = undefined
        newValues.locationId = undefined
        setFormValues(newValues)
        return
      }
      newValues = _.omit(newValues, ['memberId', 'locationId'])
      locationAutoSelectedRef.current = true

      if (isMemberLevel(currentUser) && Array.isArray(membersList) && membersList.length) {
        const autoMember = membersList.find((m) => Number(m.value) === Number(currentUser.memberId))
        if (autoMember) {
          newValues.memberId = autoMember.value
          memberAutoSelectedRef.current = true
        } else {
          memberAutoSelectedRef.current = false
        }
      } else {
        memberAutoSelectedRef.current = false
      }
    }

    if (fieldName === 'memberId') {
      newValues = _.omit(newValues, ['locationId'])
      locationAutoSelectedRef.current = true
    }

    setFormValues(newValues)
    debouncedSetValues('baseFields', newValues)
  }

  const { data: actionTypesList } = useGetActionTypesQuery({
    ...(currentUser.parentMember && { memberIds: currentUser.parentMember.id })
  })

  const { data: actionStatusList } = useGetActionStatusQuery()

  const { data: harvestsList } = useGetHarvestsQuery()

  const customersQueryParams = { actionTypeId: formValues?.actionTypeId }
  const { data: customersList } = useGetCustomersQuery(customersQueryParams, { skip: !formValues?.actionTypeId })

  const membersQueryParams = { customerId: formValues?.customerId, currentUser }
  const { data: membersList } = useGetMembersQuery(membersQueryParams, {
    skip: !formValues?.customerId && !isMemberLevel(currentUser)
  })

  const locationsQueryParams = { customerId: formValues?.customerId, memberId: formValues?.memberId, currentUser }
  const { data: locationsList, isFetching: isFetchingLocations } = useGetLocationsQuery(locationsQueryParams, {
    skip: !formValues?.customerId || (isMemberLevel(currentUser) && !formValues?.memberId)
  })

  useEffect(() => {
    locationAutoSelectedRef.current = false
  }, [locationsList])

  const plannedStatusId = useMemo(() => {
    if (!Array.isArray(actionStatusList)) return null

    const planned = actionStatusList.find(
      ({ description }) =>
        String(description || '')
          .trim()
          .toUpperCase() === 'PLANEJADA'
    )

    return planned?.value ?? null
  }, [actionStatusList])

  const latestReferencePeriodId = useMemo(() => {
    if (!Array.isArray(harvestsList) || !harvestsList.length) return null

    const parseHarvestYears = (description) => {
      const match = String(description || '').match(/(\d{4})\s*\/\s*(\d{4})/)
      if (!match) return { startYear: 0, endYear: 0 }

      return {
        startYear: Number(match[1]),
        endYear: Number(match[2])
      }
    }

    const sortedHarvests = [...harvestsList].sort((a, b) => {
      const aYears = parseHarvestYears(a.description)
      const bYears = parseHarvestYears(b.description)

      return bYears.endYear - aYears.endYear || bYears.startYear - aYears.startYear || Number(b.value) - Number(a.value)
    })

    return sortedHarvests[0]?.value ?? null
  }, [harvestsList])

  // Populate actionTypeId from forecast visit modal
  useEffect(() => {
    if (!isForecastPreload) return
    if (!Object.keys(formValues || {}).length) return

    const preloadActionTypeId = location.state?.preloadActionData?.baseFields?.actionTypeId

    if (!formValues.actionTypeId && preloadActionTypeId && Array.isArray(actionTypesList)) {
      const foundActionType = actionTypesList.find((item) => Number(item.value) === Number(preloadActionTypeId))

      if (foundActionType) {
        const nextValues = { ...formValues, actionTypeId: foundActionType.value }
        setFormValues(nextValues)
        debouncedSetValues('baseFields', nextValues)
      }
    }
  }, [isForecastPreload, formValues, actionTypesList, location.state, debouncedSetValues])

  useEffect(() => {
    if (!isForecastPreload) return
    if (!Object.keys(formValues || {}).length) return
    let nextValues = { ...formValues }
    let changed = false

    if (!nextValues.actionStatusId && plannedStatusId) {
      nextValues.actionStatusId = plannedStatusId
      changed = true
    }

    if (!nextValues.referencePeriodId && latestReferencePeriodId) {
      nextValues.referencePeriodId = latestReferencePeriodId
      changed = true
    }

    if (changed) {
      setFormValues(nextValues)
      debouncedSetValues('baseFields', nextValues)
    }
  }, [isForecastPreload, formValues, plannedStatusId, latestReferencePeriodId, debouncedSetValues])

  useEffect(() => {
    if (!isMemberLevel(currentUser)) return
    if (!Object.keys(formValues || {}).length) return

    let nextValues = { ...formValues }
    let changed = false

    if (!memberAutoSelectedRef.current && Array.isArray(membersList) && membersList.length && !formValues.memberId) {
      const autoMember = membersList.find((m) => Number(m.value) === Number(currentUser.memberId))
      if (autoMember) {
        memberAutoSelectedRef.current = true
        nextValues.memberId = autoMember.value
        changed = true
      }
    }

    if (
      !locationAutoSelectedRef.current &&
      !isFetchingLocations &&
      Array.isArray(locationsList) &&
      locationsList.length === 1 &&
      !nextValues.locationId
    ) {
      locationAutoSelectedRef.current = true
      nextValues.locationId = locationsList[0].value
      changed = true
    }

    if (changed) {
      setFormValues(nextValues)
      debouncedSetValues('baseFields', nextValues)
    }
  }, [currentUser, membersList, locationsList, isFetchingLocations, formValues, debouncedSetValues])

  const isDateRequired = useMemo(
    () =>
      [actionStatus.DONE, actionStatus.PLANNED].includes(
        actionStatusList?.find(({ value }) => value === formValues?.actionStatusId)?.description
      ),
    [actionStatusList, formValues?.actionStatusId]
  )

  const isSwitchVisible = isAdmin()

  const disabledField = isReadOnly || isPreview

  return (
    <div>
      <Row type="flex">
        <CustomInput
          disabled={disabledField || formValues['actionTemplateItemId'] != null}
          field="actionSubject"
          required
          errors={errors}
          setFieldValue={onChangeValue}
          rowSpan={12}
          value={{ actionSubject: formValues['actionSubject'] }}
          handleBlur={handleBlur}
          id="actionsDescriptionIdKey01"
        />
        <CustomSelect
          showSearch
          enable={disabledField || formValues['actionTemplateItemId'] != null || !isNewAction}
          field="actionTypeId"
          errors={errors}
          required
          setFieldValue={onChangeValue}
          rowSpan={12}
          value={{ actionTypeId: formValues['actionTypeId'] }}
          values={actionTypesList}
          handleBlur={handleBlur}
          id="actionsSelection"
        />
      </Row>

      <Row type="flex" justify="start">
        <CustomSelect
          showSearch
          enable={disabledField}
          field="actionStatusId"
          errors={errors}
          required
          setFieldValue={onChangeValue}
          rowSpan={12}
          value={{ actionStatusId: formValues['actionStatusId'] }}
          values={actionStatusList}
          handleBlur={handleBlur}
          id="actionsSelectionIdKey02"
        />
        <CustomSelect
          showSearch
          enable={!formValues['actionTypeId'] || disabledField || formValues['actionTemplateItemId'] != null}
          field="customerId"
          errors={errors}
          required
          setFieldValue={onChangeValue}
          rowSpan={12}
          value={{ customerId: formValues['customerId'] }}
          values={customersList}
          handleBlur={handleBlur}
          id="actionsSelectionIdKey03"
        />
      </Row>

      <Row type="flex" justify="start">
        <CustomSelect
          showSearch
          enable={!formValues['customerId'] || disabledField}
          field="memberId"
          errors={errors}
          required
          setFieldValue={onChangeValue}
          rowSpan={12}
          value={{ memberId: formValues['memberId'] }}
          values={membersList}
          handleBlur={handleBlur}
          id="actionsSelectionIdKey04"
        />
        <CustomSelect
          showSearch
          enable={!formValues['customerId'] || disabledField}
          field="locationId"
          errors={errors}
          required
          setFieldValue={onChangeValue}
          rowSpan={12}
          value={{ locationId: formValues['locationId'] }}
          values={locationsList}
          handleBlur={handleBlur}
          id="actionsSelectionIdKey05"
        />
      </Row>

      <Row type="flex">
        <CustomSelect
          showSearch
          enable={disabledField}
          field="referencePeriodId"
          errors={errors}
          required
          setFieldValue={onChangeValue}
          rowSpan={6}
          value={{ referencePeriodId: formValues['referencePeriodId'] }}
          values={harvestsList}
          handleBlur={handleBlur}
          id="actionsSelectionIdKey06"
          className="ant-form-item"
        />
        <CustomInput
          type="date"
          disabled={disabledField}
          field="executionDate"
          errors={errors}
          setFieldValue={onChangeValue}
          rowSpan={6}
          value={{ executionDate: formValues['executionDate'] }}
          handleBlur={handleBlur}
          required={isDateRequired}
          id="actionsSelectionIdKey06"
        />
        {/*
            [Suporte #46638] Visitas - Remover campo "Hora" do formulário principal
            USER STORY 10613 (AzureDevops)
          */}
        {/* <CustomInput
            type="hour"
            disabled={disabledField}
            field="executionTime"
            errors={errors}
            setFieldValue={onChangeValue}
            rowSpan={6}
            value={{ executionTime: formValues['executionTime'] }}
            handleBlur={handleBlur}
            required={isDateRequired}
            id="actionsDate"
          /> */}
        {isSwitchVisible && (
          <CustomSwitch
            field="active"
            disabled={disabledField}
            errors={errors}
            setFieldValue={onChangeValue}
            rowSpan={8}
            value={{ active: formValues['active'] }}
            handleBlur={handleBlur}
            unCheckedChildren={getMessage('label.inactive')}
            checkedChildren={getMessage('label.active')}
            defaultValue={true}
          />
        )}
      </Row>
    </div>
  )
}
